import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { getScheduledSlotName } from "../../lib/scheduledSlotUtilities";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import PeopleIcon from "@material-ui/icons/People";
import format from "date-fns/format";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { withStyles } from "@material-ui/core/styles";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import Paper from "@material-ui/core/Paper";

import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import { useAppContext } from "../../api/AppContext";
import Loader from "../shared/Loader";
import { useModals } from "../../hooks/useModals";
import useLocalStorage from "../../hooks/useLocalStorage";
import Timestamps from "../Timestamps";
// import MuiChip from "@material-ui/core/Chip";

const Accordion = withStyles({
  root: {
    "&:before": {
      height: 0,
    },
    backgroundColor: "transparent",
    boxShadow: "none",
    width: "100%",
  },
})(MuiAccordion);

const AccordionSummary = withStyles({
  content: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },
})(MuiAccordionSummary);

const A = styled.a`
  text-decoration: none;
`;

// const Chip = withStyles({
//   label: {
//     fontSize: "0.625rem",
//     fontWeight: "bold",
//     textTransform: "uppercase",
//   },
//   root: { cursor: "pointer", marginLeft: "0.25rem", marginRight: "0.5rem" },
// })(MuiChip);

const formatExistenceCheckedAt = (checkedAt) => {
  if (!checkedAt) {
    return null;
  }
  try {
    return format(new Date(checkedAt), "dd MMM yyyy HH:mm");
  } catch {
    return null;
  }
};

const UsersLists = () => {
  const {
    fetchListofusers,
    fetchListofusersContent,
    isListofusersContentLoading,
    listofusers,
    isListofusersLoading,
    isJourneysLoading,
    updateListofusers,
    deleteListofusers,
    fetchJourneys,
    user,
    journeys,
    myCompanies,
    isMyCompaniesLoading,
    fetchMyCompanies,
  } = useAppContext();

  const modals = useModals();
  const [dataLoad, setDataLoad] = useState(false);

  const [expanded, setExpanded] = useLocalStorage(
    `${user.sub}.Listofusers.Expanded`,
    {}
  );
  const [refreshingExistenceListId, setRefreshingExistenceListId] =
    useState(null);
  const [searchText, setSearchText] = useState("");

  const handleExpandClick = useCallback(
    (step) => () => {
      setExpanded((expanded) => ({
        ...expanded,
        [step]: !expanded[step],
      }));
    },
    [setExpanded]
  );

  useEffect(() => {
    // reload data if user goes back to the page
    setDataLoad(true);
  }, []);

  useEffect(() => {
    if (!dataLoad) {
      if (!journeys.length) {
        fetchJourneys();
      }
      if (Object.keys(myCompanies).length === 0) fetchMyCompanies();
      fetchListofusers({ withusageinfo: true });
      setDataLoad(true);
    }
  }, [
    fetchJourneys,
    fetchListofusers,
    fetchMyCompanies,
    journeys.length,
    dataLoad,
    myCompanies,
  ]);

  const handleCreate = useCallback(() => {
    modals.openModal(modals.modalTypes.EditUsersList, {
      isNew: true,
      myCompanies,
      myCompaniesIds: user.companies,
      onConfirm: (listData) =>
        updateListofusers({
          isNew: true,
          ...listData,
        }),
    });
  }, [modals, updateListofusers, myCompanies, user.companies]);

  const handleDownloadCSV = useCallback(
    (listData, refresh = false) =>
      async (e) => {
        e.stopPropagation();
        await fetchListofusersContent(
          listData.listname,
          listData.id,
          refresh,
          {}, // options
          true // get plain emails
        );
      },
    [fetchListofusersContent]
  );

  const { enqueueSnackbar } = useSnackbar();

  const handleRefreshExistenceCheck = useCallback(
    (listData) => async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setRefreshingExistenceListId(listData.id);
      try {
        const result = await fetchListofusersContent(
          listData.listname,
          listData.id,
          true,
          { forNews: true }
        );
        if (result?.listcontentextra?.checkn) {
          enqueueSnackbar("User existence check updated", {
            variant: "success",
          });
        }
      } finally {
        setRefreshingExistenceListId(null);
      }
    },
    [fetchListofusersContent, enqueueSnackbar]
  );

  const handleEdit = useCallback(
    (listData) => (e) => {
      e.stopPropagation();
      const { condition, ...list } = listData;
      modals.openModal(modals.modalTypes.EditUsersList, {
        item: list,
        myCompanies,
        myCompaniesIds: user.companies,
        onConfirm: (list) => updateListofusers(list),
      });
    },
    [modals, updateListofusers, myCompanies, user.companies]
  );

  const handleEditContent = useCallback(
    (listData) => (e) => {
      e.stopPropagation();
      modals.openModal(modals.modalTypes.EditListContent, {
        hasSnamCompany: Boolean(listData.hasSnamCompany),
        hasCdpCompany: Boolean(listData.hasCdpCompany),
        listId: listData.id,
        listname: listData.listname,
        listData,
        onConfirm: (data) => updateListofusers({ ...listData, ...data }),
      });
    },
    [modals, updateListofusers]
  );

  const handleEditScheduled = useCallback(
    ({ journeyId }) =>
      () => {
        const journey = journeys.find(({ id }) => id === journeyId);

        if (!journey) {
          enqueueSnackbar("Journey doesn't exist", { variant: "error" });
          return;
        }
        window.open(
          `/journeys/${journey.id}/${journey.locale}/scheduled-slots`,
          "_blank"
        );
      },
    [enqueueSnackbar, journeys]
  );

  const handleEditSemantic = useCallback(
    ({ journeyId, id, type }) =>
      () => {
        const journey = journeys.find(({ id }) => id === journeyId);

        if (!journey) {
          enqueueSnackbar("Journey doesn't exist", { variant: "error" });
          return;
        }
        window.open(
          `/journeys/${journeyId}/${
            journey.locale
          }/${type.toLowerCase()}s/${id}`,
          "_blank"
        );
      },
    [enqueueSnackbar, journeys]
  );

  const handleDelete = useCallback(
    ({ id, listname, condition }) =>
      (e) => {
        e.stopPropagation();
        // for a newly created list, condition is not yet set
        if (condition?.nudges?.length || condition?.scheduledSlots?.length) {
          enqueueSnackbar(
            "This List of users is used in some journeys; clear its usage to enable deletion of this record.",
            {
              variant: "error",
            }
          );
          setExpanded((expanded) => ({
            ...expanded,
            [listname]: true,
          }));
          return;
        }

        modals.openConfirmation({
          onConfirm: () => deleteListofusers({ id }),
          text: `You want to delete List "${listname}"`,
        });
      },
    [deleteListofusers, enqueueSnackbar, modals, setExpanded]
  );

  const filteredListofusers = useMemo(() => {
    if (!listofusers) {
      return [];
    }
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return listofusers;
    }
    return listofusers.filter(
      (list) =>
        list.listname?.toLowerCase().includes(query) ||
        list.listdescription?.toLowerCase().includes(query)
    );
  }, [listofusers, searchText]);

  if (
    isListofusersLoading ||
    isJourneysLoading ||
    (isListofusersContentLoading && !refreshingExistenceListId)
  ) {
    return <Loader />;
  }

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Typography variant="h6">User&#39;s Lists</Typography>
            </Grid>
            <Grid item>
              <Typography color="textSecondary" variant="body2">
                Use this section to create/edit User&#39;s Lists that will be
                available in scheduled slots and nudges conditions (can target
                messages to users that are &quot;in the list&quot; or are
                &quot;not in the list&quot;)
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          >
            <Button
              color="primary"
              disabled={isMyCompaniesLoading}
              endIcon={<AddIcon />}
              variant="contained"
              onClick={handleCreate}
            >
              Add new User&#39;s List
            </Button>
            <TextField
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by name or description"
              size="small"
              style={{ width: 280 }}
              value={searchText}
              variant="outlined"
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Box>
        </Grid>
        <Grid item>
          {filteredListofusers.length > 0 ? (
            filteredListofusers.map((list, i) => (
              <Accordion
                key={i}
                expanded={Boolean(expanded[i])}
                onChange={handleExpandClick(i)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid
                    container
                    justifyContent="space-between"
                    spacing={1}
                    style={{ width: "100%" }}
                  >
                    <Grid item style={{ flexGrow: 1 }}>
                      <Grid container direction="column" spacing={0.5}>
                        <Grid item>
                          <Typography variant="h6">{list.listname}</Typography>
                        </Grid>
                        <Grid item>
                          {list.listdescription && (
                            <Typography color="textSecondary" variant="body2">
                              {list.listdescription}
                            </Typography>
                          )}
                          {list.checkn ? (
                            <>
                              <Typography color="textPrimary" variant="body1">
                                - Users in this list: {list.nusers || 0} (
                                Existing users: {list.checkn?.nsuccess || 0};
                                Non existing users:{" "}
                                {list.checkn?.nnotfound || 0}
                                {list.checkn?.nerrors > 0 && (
                                  <>; {list.checkn.nerrors} errors</>
                                )}
                                )
                              </Typography>
                              <Box alignItems="center" display="flex">
                                <Typography
                                  color="textSecondary"
                                  style={{ marginRight: 4 }}
                                  variant="body2"
                                >
                                  User existence last checked on{" "}
                                  {formatExistenceCheckedAt(
                                    list.checkn?.checkedAt
                                  ) || "unknown"}
                                </Typography>
                                <Tooltip title="Refresh user existence check">
                                  <span>
                                    <IconButton
                                      disabled={
                                        !list.listcontent_id ||
                                        refreshingExistenceListId === list.id
                                      }
                                      size="small"
                                      type="button"
                                      onClick={handleRefreshExistenceCheck(
                                        list
                                      )}
                                    >
                                      {refreshingExistenceListId === list.id ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <RefreshIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </>
                          ) : (
                            <Typography color="textSecondary" variant="body2">
                              - Users in this list: {list.nusers || 0}
                            </Typography>
                          )}
                          {/* <Typography color="textSecondary" variant="body2">
                            - id: {list.id}
                          </Typography> */}
                          <Typography color="textSecondary" variant="body2">
                            Used for{" "}
                            {(list.condition?.nudges?.length || 0) +
                              (list.condition?.scheduledSlots?.length ||
                                0)}{" "}
                            messages
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      {!isMyCompaniesLoading && (
                        <Grid container spacing={1}>
                          <Grid item>
                            <Tooltip title="Edit List">
                              <IconButton onClick={handleEdit(list)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          <Grid item>
                            <Tooltip title="Edit List Content">
                              <IconButton
                                disabled={!list.listcontent_id}
                                onClick={handleEditContent(list)}
                              >
                                <PeopleIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          <Grid item>
                            <Tooltip title="Download CSV">
                              <IconButton
                                disabled={
                                  !list.listcontent_id ||
                                  isListofusersContentLoading
                                }
                                onClick={handleDownloadCSV(list, true)}
                              >
                                <GetAppIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          {/* Re-check and download button replaced by the main download button
                          <Grid item>
                            <Grid item>
                              <Tooltip title="Re-check user existence and Download CSV">
                                <IconButton
                                  disabled={
                                    !list.listcontent_id ||
                                    isListofusersContentLoading
                                  }
                                  onClick={handleDownloadCSV(list, true)}
                                >
                                  <RefreshIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                          */}
                          <Grid item>
                            <Tooltip title="Delete">
                              <IconButton onClick={handleDelete(list)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper style={{ width: "100%" }} variant="outlined">
                    <Box p={2}>
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <Grid item>
                            <Timestamps
                              createdAt={list.created_at}
                              createdBy={{ email: list.created_by }}
                              isIso={true}
                              updatedAt={list.updated_at}
                              updatedBy={{ email: list.updated_by }}
                            />
                          </Grid>
                          <br></br>
                          <Grid item>
                            <Typography color="textSecondary" variant="body2">
                              Special conditions:
                            </Typography>
                            <List
                              subheader={
                                <ListSubheader>
                                  <b>Scheduled slots</b>
                                </ListSubheader>
                              }
                            >
                              {list.condition?.scheduledSlots?.map(
                                ({
                                  id,
                                  datePeriod,
                                  datetimeStart,
                                  datetimeEnd,
                                  journeyId,
                                  journey,
                                  specificInterval,
                                }) => (
                                  <ListItem key={id}>
                                    <ListItemText
                                      primary={
                                        <>
                                          {journey && (
                                            <>
                                              <A
                                                href={`/journeys/${journey.id}/${journey.locale}/scheduled-slots`}
                                                rel="noreferrer noopener"
                                                target="_blank"
                                              >
                                                (
                                                {journey.internalName ||
                                                  journey.name}
                                                )
                                              </A>{" "}
                                            </>
                                          )}
                                          {getScheduledSlotName({
                                            datePeriod,
                                            datetimeEnd,
                                            datetimeStart,
                                            specificInterval,
                                          })}
                                        </>
                                      }
                                      // secondary={text}
                                    />
                                    <ListItemSecondaryAction>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={handleEditScheduled({
                                            journeyId,
                                          })}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                )
                              )}
                            </List>
                            <List
                              subheader={
                                <ListSubheader>
                                  <b>Nudges</b>
                                </ListSubheader>
                              }
                            >
                              {list.condition?.nudges?.map(
                                ({
                                  id,
                                  title,
                                  text,
                                  journeyId,
                                  type,
                                  journey,
                                }) => (
                                  <ListItem key={id}>
                                    <ListItemText
                                      primary={
                                        <>
                                          {journey && (
                                            <>
                                              <A
                                                href={`/journeys/${journey.id}/${journey.locale}`}
                                                rel="noreferrer noopener"
                                                target="_blank"
                                              >
                                                (
                                                {journey.internalName ||
                                                  journey.name}
                                                )
                                              </A>{" "}
                                            </>
                                          )}
                                          {title}
                                        </>
                                      }
                                      secondary={text}
                                    />
                                    <ListItemSecondaryAction>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={handleEditSemantic({
                                            id,
                                            journeyId,
                                            type,
                                          })}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                )
                              )}
                            </List>
                          </Grid>
                        </Grid>
                        <Grid item></Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            ))
          ) : searchText.trim() && listofusers?.length > 0 ? (
            <Typography color="textSecondary" variant="body2">
              No lists match your search.
            </Typography>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsersLists;
